import { DeleteOutlined, DragOutlined } from "@ant-design/icons";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useMemo } from "react";
import { Locale } from "../../utils";
import ImageUpload from "./ImageUpload";
import "./ProductDetailImagesEditor.scss";

const i18n = Locale.getInstance();

function pickPath(item) {
  if (!item) return "";
  if (typeof item === "string") return item;
  return item.file_path || item.path || item.url || "";
}

function pickUrl(item) {
  if (!item) return "";
  if (typeof item === "string") return item;
  return item.url || item.file_path || item.path || "";
}

function normalizeList(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((item, index) => {
      const path = pickPath(item);
      const url = pickUrl(item);
      if (!path && !url) return null;
      return {
        id: `${path || url}-${index}`,
        path: path || url,
        url: url || path,
      };
    })
    .filter(Boolean);
}

function SortableImageCard({ item, onRemove }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="product-detail-images-editor__card"
    >
      <div
        className="product-detail-images-editor__handle"
        {...attributes}
        {...listeners}
      >
        <DragOutlined />
      </div>
      <div className="product-detail-images-editor__thumb">
        <img src={item.url} alt="" />
      </div>
      <button
        type="button"
        className="product-detail-images-editor__remove"
        onClick={() => onRemove(item.id)}
      >
        <DeleteOutlined />
      </button>
    </div>
  );
}

/**
 * Ordered product-detail gallery: media-library upload + drag sort + delete.
 * value / onChange use [{ path, url }] (or path strings).
 */
export default function ProductDetailImagesEditor({ value, onChange }) {
  const items = useMemo(() => normalizeList(value), [value]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const emit = (next) => {
    onChange?.(
      next.map(({ path, url }) => ({
        path,
        url,
      }))
    );
  };

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    emit(arrayMove(items, oldIndex, newIndex));
  };

  const onRemove = (id) => {
    emit(items.filter((i) => i.id !== id));
  };

  const onAddFromLibrary = (selected) => {
    const incoming = normalizeList(
      Array.isArray(selected) ? selected : selected ? [selected] : []
    );
    if (!incoming.length) return;
    const existingPaths = new Set(items.map((i) => i.path));
    const merged = [...items];
    incoming.forEach((item) => {
      if (!existingPaths.has(item.path)) {
        existingPaths.add(item.path);
        merged.push(item);
      }
    });
    emit(merged);
  };

  return (
    <div className="product-detail-images-editor">
      <p className="product-detail-images-editor__hint">
        {i18n.t("productEdit.detailImagesHint")}
      </p>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={items.map((i) => i.id)}
          strategy={rectSortingStrategy}
        >
          <div className="product-detail-images-editor__grid">
            {items.map((item) => (
              <SortableImageCard
                key={item.id}
                item={item}
                onRemove={onRemove}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {!items.length ? (
        <div className="product-detail-images-editor__empty">
          {i18n.t("productEdit.detailImagesEmpty")}
        </div>
      ) : null}

      <div className="product-detail-images-editor__upload">
        <ImageUpload
          images={[]}
          multiple
          setSelectedImages={onAddFromLibrary}
        />
      </div>
    </div>
  );
}

export function detailImagesToPaths(list) {
  return normalizeList(list)
    .map((i) => i.path)
    .filter(Boolean);
}
